import * as React from 'react'
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'
import * as styles from './AlertDialog.css'
import type { ActionButtonVariants } from './AlertDialog.css'

// =============================================================================
// AlertDialog Root
// =============================================================================

type AlertDialogRootProps = AlertDialogPrimitive.AlertDialogProps

const AlertDialogRoot = AlertDialogPrimitive.Root

// =============================================================================
// AlertDialog Trigger
// =============================================================================

type AlertDialogTriggerProps = AlertDialogPrimitive.AlertDialogTriggerProps

const AlertDialogTrigger = AlertDialogPrimitive.Trigger

// =============================================================================
// AlertDialog Portal
// =============================================================================

type AlertDialogPortalProps = AlertDialogPrimitive.AlertDialogPortalProps

const AlertDialogPortal = AlertDialogPrimitive.Portal

// =============================================================================
// AlertDialog Overlay
// =============================================================================

type AlertDialogOverlayProps = AlertDialogPrimitive.AlertDialogOverlayProps

const AlertDialogOverlay = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Overlay>,
  AlertDialogOverlayProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    ref={ref}
    className={`${styles.overlay} ${className ?? ''}`}
    {...props}
  />
))

AlertDialogOverlay.displayName = 'AlertDialog.Overlay'

// =============================================================================
// AlertDialog Content
// =============================================================================

type AlertDialogContentProps = AlertDialogPrimitive.AlertDialogContentProps

const AlertDialogContent = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Content>,
  AlertDialogContentProps
>(({ className, children, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={`${styles.content} ${className ?? ''}`}
      {...props}
    >
      {children}
    </AlertDialogPrimitive.Content>
  </AlertDialogPortal>
))

AlertDialogContent.displayName = 'AlertDialog.Content'

// =============================================================================
// AlertDialog Title
// =============================================================================

type AlertDialogTitleProps = AlertDialogPrimitive.AlertDialogTitleProps

const AlertDialogTitle = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Title>,
  AlertDialogTitleProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={`${styles.title} ${className ?? ''}`}
    {...props}
  />
))

AlertDialogTitle.displayName = 'AlertDialog.Title'

// =============================================================================
// AlertDialog Description
// =============================================================================

type AlertDialogDescriptionProps = AlertDialogPrimitive.AlertDialogDescriptionProps

const AlertDialogDescription = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Description>,
  AlertDialogDescriptionProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={`${styles.description} ${className ?? ''}`}
    {...props}
  />
))

AlertDialogDescription.displayName = 'AlertDialog.Description'

// =============================================================================
// AlertDialog Actions Container
// =============================================================================

type AlertDialogActionsProps = React.HTMLAttributes<HTMLDivElement>

const AlertDialogActions = React.forwardRef<HTMLDivElement, AlertDialogActionsProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={`${styles.actions} ${className ?? ''}`} {...props} />
  )
)

AlertDialogActions.displayName = 'AlertDialog.Actions'

// =============================================================================
// AlertDialog Cancel
// =============================================================================

type AlertDialogCancelProps = AlertDialogPrimitive.AlertDialogCancelProps

const AlertDialogCancel = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Cancel>,
  AlertDialogCancelProps
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={`${styles.actionButton({ variant: 'cancel' })} ${className ?? ''}`}
    {...props}
  />
))

AlertDialogCancel.displayName = 'AlertDialog.Cancel'

// =============================================================================
// AlertDialog Action
// =============================================================================

type AlertDialogActionProps = AlertDialogPrimitive.AlertDialogActionProps &
  NonNullable<ActionButtonVariants>

const AlertDialogAction = React.forwardRef<
  React.ComponentRef<typeof AlertDialogPrimitive.Action>,
  AlertDialogActionProps
>(({ variant = 'action', className, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={`${styles.actionButton({ variant })} ${className ?? ''}`}
    {...props}
  />
))

AlertDialogAction.displayName = 'AlertDialog.Action'

// =============================================================================
// Exports
// =============================================================================

export const AlertDialog = Object.assign(AlertDialogRoot, {
  Root: AlertDialogRoot,
  Trigger: AlertDialogTrigger,
  Portal: AlertDialogPortal,
  Overlay: AlertDialogOverlay,
  Content: AlertDialogContent,
  Title: AlertDialogTitle,
  Description: AlertDialogDescription,
  Actions: AlertDialogActions,
  Cancel: AlertDialogCancel,
  Action: AlertDialogAction,
})

export type {
  AlertDialogRootProps,
  AlertDialogTriggerProps,
  AlertDialogPortalProps,
  AlertDialogOverlayProps,
  AlertDialogContentProps,
  AlertDialogTitleProps,
  AlertDialogDescriptionProps,
  AlertDialogActionsProps,
  AlertDialogCancelProps,
  AlertDialogActionProps,
}
